import * as platform from '@platform';
import type { ApiError as ApiErrorPayload } from '@worki/shared';
import { authedFetch, RequestError } from '../../lib/api.js';
import { isNative } from '../../lib/config.js';

/**
 * 1.44 — "שיתוף" on a stored document.
 *
 * What is handed on is the file itself, not a link to it: a document in the
 * library is private, and an address that opens it for whoever holds it would
 * be the one thing the library is built not to have.
 *
 * In the app that is the phone's own share sheet, which is where `saveBytes`
 * ends. In a browser it is the Web Share API when the browser says it can share
 * a file, and a download — exactly what "הורדה" does — when it cannot.
 */
export interface ShareHost {
  /** The app, where `saveBytes` is the way out whatever the browser API would say. */
  native: boolean;
  canShareFiles: (files: File[]) => boolean;
  shareFiles: (files: File[], title: string) => Promise<void>;
  saveBytes: (blob: Blob, filename: string) => Promise<void>;
}

export type ShareOutcome = 'shared' | 'saved' | 'dismissed';

export async function shareFile(file: File, title: string, host: ShareHost): Promise<ShareOutcome> {
  if (!host.native && host.canShareFiles([file])) {
    try {
      await host.shareFiles([file], title);
      return 'shared';
    } catch (error) {
      // Closing the sheet is an answer, not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return 'dismissed';
      // Anything else is the browser declining — Safari wants the share to start
      // inside the tap, and the file had to be fetched first. The download is
      // what the owner gets instead of nothing.
    }
  }
  await host.saveBytes(file, file.name);
  return 'saved';
}

/**
 * 1.59 — the one PDF the server makes of several documents: their pages, one
 * document after the other, in the order the list shows them
 * (`POST /library/bulk/merge`).
 *
 * Not `api.post`, which reads a JSON answer and drops any other, and not
 * `api.uploadBlob`, which sends a form: the ids go as JSON and the answer is the
 * bytes. A refusal is a JSON body like every other route's, and is thrown as the
 * `RequestError` the screen already puts on a toast, key and all.
 */
export async function mergedPdf(ids: string[], send: typeof authedFetch = authedFetch): Promise<Blob> {
  const response = await send('/library/bulk/merge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? ((await response.json()) as ApiErrorPayload | null) : null;
    throw new RequestError(response.status, payload?.error?.code ?? 'errors.unknown', payload?.error?.fields ?? {});
  }
  return response.blob();
}

/**
 * 1.59 — several documents handed on as the one file they merge into, the way a
 * single one is (`shareFile`): the phone's share sheet in the app, the Web Share
 * API or a download in a browser. One file under a name of its own — none of the
 * documents' names is the merged file's — and neither a link nor a file apiece.
 *
 * A refusal from the server throws before anything is handed on.
 */
export async function shareMerged(
  ids: string[],
  name: string,
  host: ShareHost,
  merged: (ids: string[]) => Promise<Blob> = mergedPdf,
): Promise<ShareOutcome> {
  const file = new File([await merged(ids)], `${name}.pdf`, { type: 'application/pdf' });
  return shareFile(file, name, host);
}

/** The page's own: the platform's `saveBytes`, and the browser's Web Share API. */
export function browserShareHost(): ShareHost {
  return {
    native: isNative(),
    canShareFiles: (files) =>
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files }),
    shareFiles: (files, title) => navigator.share({ files, title }),
    saveBytes: platform.saveBytes,
  };
}
